import { useEffect, useState, type PropsWithChildren } from 'react';

import { initI18n, i18nInstance } from '@sshop/i18n';
import { isLocaleCode } from '@sshop/domain';

import { env } from '@/shared/config/env';
import { api } from '@/shared/lib/apiClient';
import { AppLoader } from '@/shared/ui/AppLoader';

const resources = {
  ge: {
    common: {
      'nav.home': 'მთავარი',
      'nav.catalog': 'კატალოგი',
      'nav.categories': 'კატეგორიები',
      'nav.allCategories': 'ყველა კატეგორია',
      'nav.allProducts': 'ყველა პროდუქტი',
      'nav.deals': 'შეთავაზებები',
      'nav.categoriesNoSubs': 'ქვეკატეგორიები არ მოიძებნა',
      'nav.categoriesCount_one': '{{count}} პროდუქტი',
      'nav.categoriesCount_other': '{{count}} პროდუქტი',
      'nav.categoriesCount': '{{count}} პროდუქტი',
      'nav.categoriesPickSub': 'აირჩიე ქვეკატეგორია',
      'nav.categoriesFeatured': 'რჩეული შეთავაზება',
      'nav.categoriesHint': 'ააწერე კატეგორიას მენიუს გასახსნელად',
      'nav.categoriesSeeAllSubs': 'ყველა ქვეკატეგორიის ნახვა',
      'nav.categoriesViewAllProducts': 'ყველა პროდუქტის ნახვა',
      'nav.back': 'უკან',
      'nav.close': 'დახურვა',
      'nav.cart': 'კალათა',
      'nav.compare': 'პროდუქტების შედარება',
      'nav.compareCount': 'შედარება ({{count}})',
      'nav.about': 'ჩვენ შესახებ',
      'nav.news': 'სიახლეები',
      'nav.contact': 'კონტაქტი',
      'nav.search': 'ძებნა',
      'nav.favorites': 'სასურველი',
      'footer.tagline': 'შენი სანდო ონლაინ მაღაზია.',
      'footer.shop': 'ყიდვა',
      'footer.browse': 'დათვალიერება',
      'footer.info': 'ინფორმაცია',
      'footer.legal': 'სამართლებრივი',
      'footer.guarantee': 'გარანტია',
      'footer.shippingPayment': 'მიწოდება და გადახდა',
      'footer.privacy': 'კონფიდენციალურობის პოლიტიკა',
      'footer.terms': 'წესები და პირობები',
      'footer.contact': 'დაგვიკავშირდი',
      'footer.contactHint': 'საკონტაქტო ინფორმაცია მალე დაემატება.',
      'footer.shopDeals': 'შეთავაზებების ნახვა',
      'footer.rights': 'ყველა უფლება დაცულია.',
      'search.empty': 'პროდუქცია ვერ მოიძებნა',
      'search.loading': 'იძებნება…',
      'search.viewAll': 'ყველა შედეგის ნახვა',
      'search.recent': 'ბოლო ძიებები',
      'search.clearHistory': 'ისტორიის გასუფთავება',
      'search.noRecent': 'ძიების ისტორია ცარიელია',
      'wishlist.title': 'სასურველი პროდუქცია',
      'wishlist.subtitle': 'შენახული პროდუქცია მოგვიანებით განსახილველად.',
      'wishlist.empty': 'სასურველი სია ცარიელია',
      'wishlist.moveAllToCart': 'ყველაფერი კალათაში',
      'wishlist.emptyHint': 'დაამატე პროდუქცია გულის ხატულაზე დაჭერით.',
      'wishlist.browseCatalog': 'კატალოგის დათვალიერება',
      'wishlist.error': 'სიის ჩატვირთვა ვერ მოხერხდა',
      'theme.toggle': 'თემის შეცვლა',
      'theme.label': 'თემა',
      'theme.light': 'ღია',
      'theme.dark': 'მუქი',
      'theme.system': 'სისტემური',
      'app.loading': 'იტვირთება…',
      'app.error': 'შეცდომა',
      'app.notFound': 'გვერდი ვერ მოიძებნა',
      'app.retry': 'ხელახლა ცდა',
      'auth.login': 'შესვლა',
      'auth.logout': 'გასვლა',
      accountDetails: 'პირადი მონაცემები',
      'account.menu': 'ანგარიში',
      'account.preferences': 'პარამეტრები',
      'language.label': 'ენა',
      'language.english': 'ინგლისური',
      'language.georgian': 'ქართული',
      'language.shortGe': 'ქარ',
      'language.shortEn': 'EN',
      'language.switchTo': 'ენის შეცვლა: {{language}}',
      'cookies.preferences': 'ქუქიების პარამეტრები',
      'cookies.description':
        'ანალიტიკურ და სარეკლამო ქუქიებს ვიყენებთ მხოლოდ თქვენი თანხმობით. აუცილებელი ფუნქციები თანხმობის გარეშეც მუშაობს.',
      'cookies.reject': 'უარყოფა',
      'cookies.accept': 'დათანხმება',
      'home.hero.badge': 'ახალი კოლექცია',
      'home.hero.title': 'იყიდე ჭკვიანად. იყიდე SSHOP-ზე.',
      'home.hero.subtitle':
        'საქართველოს მაღაზია, რომელიც ერთ ადგილზე გთავაზობთ ათასობით პროდუქტს, სწრაფ მიწოდებას და გადახდის მოქნილ მეთოდებს.',
      'home.hero.ctaPrimary': 'კატალოგი',
      'home.hero.ctaSecondary': 'შეთავაზებები',
      'home.trust.shipping.title': 'მიწოდება და გადახდა',
      'home.trust.shipping.body': 'მიწოდებისა და გადახდის პირობები',
      'home.trust.guarantee.title': 'გარანტია',
      'home.trust.guarantee.body': 'საგარანტიო პირობები',
      'home.trust.privacy.title': 'კონფიდენციალურობა',
      'home.trust.privacy.body': 'პერსონალური მონაცემების შესახებ',
      'home.trust.terms.title': 'წესები და პირობები',
      'home.trust.terms.body': 'გაეცანი შეძენის პირობებს',
      'home.trust.returns.title': '14 დღიანი დაბრუნება',
      'home.trust.returns.body': 'უპრობლემოდ, საფასურის გარეშე',
      'home.trust.support.title': '24/7 მხარდაჭერა',
      'home.trust.support.body': 'ჩვენ ყოველთვის გვერდით ვართ',
      'home.trust.secure.title': 'უსაფრთხო გადახდა',
      'home.trust.secure.body': 'დაცული გარიგებები ბანკის დონეზე',
      'home.categories.title': 'კატეგორიები',
      'home.categories.subtitle': 'იპოვე საჭირო ნივთი სწრაფად',
      'home.categories.viewAll': 'ყველა კატეგორია',
      'home.categories.error': 'კატეგორიების ჩატვირთვა ვერ მოხერხდა',
      'categories.subtitle': 'აირჩიე კატეგორია და დაათვალიერე მისი პროდუქტები.',
      'categories.empty': 'კატეგორიები ჯერ არ არის ხელმისაწვდომი.',
      'home.featured.subtitle': 'ფასდაკლება და უახლესი მოდელები',
      'home.featured.viewAll': 'ყველას ნახვა',
      'home.carousel.promotions': 'შეთავაზებები',
      'home.carousel.slide': 'სლაიდი {{number}}',
      'home.carousel.previous': 'წინა სლაიდი',
      'home.carousel.next': 'შემდეგი სლაიდი',
      'home.carousel.selector': 'სლაიდის არჩევა',
      'home.carousel.goTo': 'სლაიდზე გადასვლა {{number}}',
      'home.carousel.play': 'სლაიდების გაშვება',
      'home.carousel.pause': 'სლაიდების შეჩერება',
      'home.values.shipping.title': 'სწრაფი მიწოდება',
      'home.values.shipping.body': 'საქართველოს მთელ ტერიტორიაზე 1–3 დღეში.',
      'home.values.returns.title': 'მარტივი დაბრუნება',
      'home.values.returns.body': '14 დღე ნივთის უპრობლემო დაბრუნებისთვის.',
      'home.values.support.title': 'მხარდაჭერა',
      'home.values.support.body': 'ჩვენი გუნდი ხელმისაწვდომია კვირაში 7 დღე.',
      'home.featured.title': 'რჩეული პროდუქცია',
      'home.featured.seeAll': 'ყველას ნახვა',
      'home.featured.placeholderCategory': 'კატეგორია',
      'home.featured.placeholderName': 'პროდუქტის სახელი',
      'home.bestPrices': 'საუკეთესო ფასები',
      'home.bestPricesSubtitle': 'შეარჩიე საუკეთესო ფასდაკლებები',
      'home.popular': 'პოპულარული პროდუქცია',
      'home.popularSubtitle': 'მომხმარებლების ფავორიტი ნივთები',
      'home.newArrivals': 'ახალი პროდუქტები',
      'home.newArrivalsSubtitle': 'ახლახან დამატებული პროდუქცია',
      'home.partners': 'ჩვენი პარტნიორები',
      'home.seeAll': 'ყველას ნახვა',
      'home.learnMore': 'გაიგე მეტი',
    },
    catalog: {
      title: 'კატალოგი',
      results: '{{count}} პროდუქტი',
      noResults: 'პროდუქტი არ მოიძებნა',
      noResultsHelp: 'სცადეთ სხვა საძიებო სიტყვა ან წაშალეთ ფილტრები.',
      browseAll: 'ყველა პროდუქტის ნახვა',
      compareProducts: 'პროდუქტების შედარება',
      compareCount: 'შედარებისთვის არჩეულია {{count}} პროდუქტი',
      clearComparison: 'შედარების გასუფთავება',
      compareSameCategory: 'შეადარეთ ერთი კატეგორიის პროდუქტები.',
      compareLimit:
        'შედარების სიაში უკვე {{count}} პროდუქტია. ახლის დასამატებლად წაშალეთ ერთ-ერთი.',
      compareNeedTwo: 'შედარებისთვის აირჩიეთ სულ მცირე 2 პროდუქტი ერთი კატეგორიიდან.',
      compareDifferences: 'მხოლოდ განსხვავებების ჩვენება',
      compareSpecification: 'მახასიათებელი',
      comparePrice: 'ფასი',
      compareStock: 'მარაგი',
      removeCompare: 'წაშლა',
      filters: 'ფილტრები',
      applyFilters: 'გაფილტვრა',
      activeFilters: 'აქტიური ფილტრები: {{count}}',
      removeFilter: 'ფილტრის წაშლა',
      clearFilters: 'გასუფთავება',
      sortBy: 'სორტირება',
      'sort.relevance': 'რელევანტური',
      'sort.popularity': 'პოპულარობის მიხედვით',
      'sort.priceAsc': 'ფასი: დაბლიდან მაღლა',
      'sort.priceDesc': 'ფასი: მაღლიდან დაბლა',
      'sort.newest': 'ახალი პროდუქცია',
      'sort.oldest': 'ძველი პროდუქცია',
      priceRange: 'ფასი',
      priceMin: 'მინ.',
      priceMax: 'მაქს.',
      brand: 'ბრენდი',
      category: 'კატეგორია',
      allCategories: 'ყველა კატეგორია',
      allProducts: 'ყველა პროდუქტი',
      inStockOnly: 'მხოლოდ მარაგში',
      filterPopular: 'პოპულარული',
      filterSpecial: 'ფასდაკლებები',
      filterNew: 'ახალი',
      subcategories: 'ქვეკატეგორიები',
      categoryDescription: 'კატეგორიის შესახებ',
      showMore: 'მეტის ნახვა',
      showLess: 'ნაკლების ნახვა',
      resetColor: 'ფერის გასუფთავება',
      perPage: 'გვერდზე',
      page: 'გვერდი {{page}}',
      prev: 'წინა',
      next: 'შემდეგი',
      loading: 'პროდუქცია იტვირთება…',
      errorLoading: 'პროდუქციის ჩატვირთვა ვერ მოხერხდა',
    },
    product: {
      variants: 'ვარიანტები',
      chooseColor: 'აირჩიეთ ფერი',
      compare: 'შედარება',
      removeCompare: 'შედარებიდან წაშლა',
      compareSameCategory: 'შეადარეთ ერთი კატეგორიის პროდუქტები.',
      compareLimit:
        'შედარების სიაში უკვე {{count}} პროდუქტია. ახლის დასამატებლად წაშალეთ ერთ-ერთი.',
      imageEnlarge: 'სურათის გადიდება: {{title}}',
      imagePrevious: 'წინა სურათი',
      imageNext: 'შემდეგი სურათი',
      imageClose: 'სურათის დახურვა',
      warrantyInfo: 'გარანტიის ინფორმაცია',
      deliveryHelp: 'მიწოდების შესახებ კითხვა',
      addToCart: 'კალათაში დამატება',
      addedToCart: 'პროდუქტი დაემატა კალათაში',
      addToWishlist: 'ფავორიტებში დამატება',
      removeFromWishlist: 'ფავორიტებიდან წაშლა',
      addedToWishlist: 'პროდუქტი დაემატა ფავორიტებში',
      outOfStock: 'მარაგი ამოიწურა',
      inStock: 'მარაგშია',
      lowStock: 'მცირე რაოდენობით',
      'stock.count': 'მარაგში {{count}} ცალი',
      brand: 'ბრენდი',
      sku: 'კოდი',
      description: 'აღწერა',
      specs: 'მახასიათებლები',
      keySpecs: 'მთავარი მახასიათებლები',
      viewAllSpecs: 'ყველა მახასიათებლის ნახვა',
      showMore: 'მეტის ნახვა',
      showLess: 'ნაკლების ნახვა',
      reviews: 'მიმოხილვები',
      rating: 'შეფასება',
      'rating.count': '{{count}} შეფასება',
      share: 'გაზიარება',
      relatedProducts: 'მსგავსი პროდუქცია',
      similarProducts: 'შეიძლება მოგეწონოს',
      viewMore: 'მეტის ნახვა',
      freeShipping: 'უფასო მიწოდება',
      quantity: 'რაოდენობა',
      installment: 'განვადება',
      buyNow: 'ახლავე ყიდვა',
      notFound: 'პროდუქტი ვერ მოიძებნა',
      home: 'მთავარი',
      catalog: 'კატალოგი',
      copyLink: 'ბმულის კოპირება',
      linkCopied: 'ბმული დაკოპირებულია',
    },
    cart: {
      title: 'შენი კალათა',
      empty: 'შენი კალათა ცარიელია',
      emptyCta: 'დაიწყე ყიდვა',
      itemCount_one: '{{count}} პროდუქტი',
      itemCount_other: '{{count}} პროდუქტი',
      subtotal: 'ჯამი',
      discount: 'ფასდაკლება',
      shipping: 'მიწოდება',
      shippingCalculatedAtCheckout: 'ფასი გამოჩნდება ქალაქის არჩევის შემდეგ',
      tax: 'დღგ',
      total: 'საბოლოო ღირებულება',
      totalBeforeShipping: 'ჯამი მიწოდებამდე',
      estimatedTotal: 'სავარაუდო ჯამი',
      free: 'უფასო',
      checkout: 'შეკვეთის გაფორმება',
      continueShopping: 'ყიდვის გაგრძელება',
      removeItem: 'წაშლა',
      updateQty: 'რაოდენობის შეცვლა',
      applyPromo: 'პრომოკოდის გამოყენება',
      promoCode: 'პრომოკოდი',
      promoCodePlaceholder: 'შეიყვანე კოდი',
      promoInvalid: 'პრომოკოდი არასწორია',
      promoApplied: 'პრომოკოდი გამოყენებულია',
      removePromo: 'პრომოკოდის წაშლა',
      orderSummary: 'შეკვეთის შეჯამება',
      saving: 'ინახება…',
      errorUpdating: 'ცვლილების შენახვა ვერ მოხერხდა',
    },
    checkout: {
      estimateDelivery: 'მიწოდების ღირებულების ნახვა',
      deliveryCost: 'მიწოდება',
      deliveryCostAfterCity: 'მიწოდების ფასი გამოჩნდება ქალაქის არჩევის შემდეგ.',
      title: 'შეკვეთის გაფორმება',
      contactInfo: 'საკონტაქტო ინფორმაცია',
      shippingAddress: 'მიწოდების მისამართი',
      paymentMethod: 'გადახდის მეთოდი',
      choosePaymentMethod: 'აირჩიე გადახდის მეთოდი',
      orderSummary: 'შეკვეთის შეჯამება',
      recipientName: 'მიმღების სახელი',
      phone: 'ტელეფონი',
      email: 'ელფოსტა',
      country: 'ქვეყანა',
      city: 'ქალაქი',
      selectCity: 'აირჩიე ქალაქი',
      street: 'მისამართი',
      pinDeliveryLocation: 'მონიშნე ზუსტი ადგილი რუკაზე',
      pinDeliveryLocationHint:
        'კურიერი უფრო სწრაფად გიპოვის. მოძებნე მისამართი ან დასვი ნიშნული რუკაზე.',
      optional: 'არასავალდებულო',
      searchAddress: 'მისამართის ძიება',
      searchAddressAction: 'მისამართის მოძებნა',
      addressSuggestions: 'მისამართების შემოთავაზებები',
      noAddressResults: 'მისამართი ვერ მოიძებნა. სცადე სხვა ფორმულირება.',
      useMyLocation: 'ჩემი მდებარეობა',
      deliveryMap: 'მიწოდების მისამართის რუკა',
      mapLoading: 'რუკა იტვირთება…',
      mapUnavailable: 'რუკა დროებით მიუწვდომელია',
      mapUnavailableHint:
        'მიუთითე მიწოდების მისამართი ზემოთ მოცემულ ველში. შეკვეთის გაფორმება კვლავ შეგიძლია.',
      tapMapToPin: 'დააჭირე რუკას ნიშნულის დასასმელად',
      mapPinTitle: 'მიწოდების ადგილი',
      searchingAddress: 'ვეძებთ მისამართს…',
      findingLocation: 'ვადგენთ შენს მდებარეობას…',
      findingAddress: 'ვადგენთ მისამართს…',
      mapLocationError: 'მდებარეობა ვერ განისაზღვრა. შეგიძლია მისამართი ხელით შეიყვანო.',
      locationConfirmed: 'მდებარეობა მონიშნულია',
      removePin: 'წაშლა',
      apartment: 'ბინა / ოფისი',
      postalCode: 'საფოსტო ინდექსი',
      notes: 'დამატებითი ინფორმაცია',
      placeOrder: 'გადახდაზე გადასვლა',
      processing: 'მუშავდება…',
      preparingPayment: 'ვქმნით გადახდის მოთხოვნას…',
      redirectingToBank: 'ბანკის გვერდზე გადამისამართება…',
      paymentCod: 'გადახდა ადგილზე',
      paymentBog: 'საქართველოს ბანკი',
      paymentBogCard: 'საქართველოს ბანკის ბარათი',
      paymentApplePay: 'Apple Pay',
      paymentGooglePay: 'Google Pay',
      paymentMobileBank: 'BOG Mobile Bank',
      paymentBogBnpl: 'ნაწილ-ნაწილ',
      paymentBogLoyalty: 'MR / PLUS',
      paymentTbc: 'თიბისი ბანკი',
      paymentBogInstallment: 'BOG განვადება',
      paymentTbcInstallment: 'TBC განვადება',
      paymentSpace: 'სფეის ბანკი',
      paymentTera: 'ტერა ბანკი',
      paymentGroupDirect: 'გადახდა ბარათით',
      paymentGroupInstallment: 'განვადება',
      paymentGroupBnpl: 'გადაიხადე მოგვიანებით',
      paymentGroupLoyalty: 'ლოიალობა',
      selected: 'არჩეულია',
      savedCard: 'ბარათი',
      newCard: 'ახალი ბარათი',
      saveCard: 'ბარათის დამახსოვრება',
      removeSavedCard: 'დამახსოვრებული ბარათის წაშლა',
      acceptTerms: 'ვეთანხმები',
      termsAndConditions: 'წესებსა და პირობებს',
      requiredField: 'ეს ველი სავალდებულოა',
      selectPaymentMethod: 'აირჩიე გადახდის მეთოდი',
      acceptTermsError: 'გადახდამდე დაეთანხმე წესებსა და პირობებს',
      paymentUnavailable: 'გადახდის სერვისი დროებით მიუწვდომელია. სცადე თავიდან.',
      bnplUnavailable: 'ნაწილ-ნაწილ გადახდა ამჟამად მიუწვდომელია. აირჩიე გადახდის სხვა მეთოდი.',
      phoneInvalid: 'შეიყვანე სწორი ტელეფონის ნომერი',
      emailInvalid: 'შეიყვანე სწორი ელფოსტა',
      cartEmpty: 'შენი კალათა ცარიელია',
      errorPlacingOrder: 'შეკვეთის გაგზავნა ვერ მოხერხდა',
      paymentFailed: 'გადახდა ვერ შესრულდა. გთხოვთ სცადოთ თავიდან.',
      paymentPending: 'გადახდა ჯერ მუშავდება. ამ გვერდზე სტატუსს ავტომატურად ვამოწმებთ.',
      successTitle: 'შეკვეთა მიღებულია',
      successBody: 'შენი შეკვეთის ნომერი: {{number}}',
      paidTitle: 'გადახდა დადასტურებულია',
      paidBody: 'თქვენი შეკვეთა მუშავდება.',
      paymentFailedTitle: 'გადახდა ვერ შესრულდა',
      paymentFailedBody:
        'შეკვეთა არ არის გადახდილი. პროდუქტები კალათაში რჩება. დაბრუნდით შეკვეთის გაფორმებაზე ან დახმარებისთვის დაგვიკავშირდით შეკვეთის ნომრით.',
      paymentPendingTitle: 'გადახდა მოწმდება',
      codPendingBody: 'შეკვეთა მიღებულია. თანხას გადაიხდით მიწოდებისას.',
      checkingPayment: 'ვამოწმებთ შეკვეთის სტატუსს…',
      unverifiedTitle: 'შეკვეთის დადასტურება ვერ მოხერხდა',
      unverifiedBody: 'ამ ბმულით შეკვეთის სტატუსს ვერ ვამოწმებთ. დახმარებისთვის დაგვიკავშირდით.',
      contactSupport: 'დაგვიკავშირდით',
      backToCheckout: 'დაბრუნება შეკვეთის გაფორმებაზე',
      backToCatalog: 'ყიდვის გაგრძელება',
    },
    auth: {
      signIn: 'შესვლა',
      close: 'დახურვა',
      checkoutPrompt: 'შეკვეთის გასაფორმებლად შედით ან შექმენით ანგარიში.',
      signUp: 'რეგისტრაცია',
      createAccount: 'ანგარიშის შექმნა',
      signingIn: 'შესვლა…',
      signingUp: 'რეგისტრაცია…',
      email: 'ელფოსტა',
      password: 'პაროლი',
      passwordRepeat: 'გაიმეორე პაროლი',
      firstName: 'სახელი',
      lastName: 'გვარი',
      personalId: 'პირადი ნომერი',
      phone: 'ტელეფონი',
      requiredField: 'ეს ველი სავალდებულოა.',
      personalIdInvalid: 'პირადი ნომერი უნდა შეიცავდეს ზუსტად 11 ციფრს',
      phoneInvalid: 'ტელეფონი უნდა შეიცავდეს ზუსტად 9 ციფრს',
      emailInvalid: 'შეიყვანეთ სწორი ელფოსტა',
      emailHint: 'გამოიყენეთ მოქმედი ელფოსტა, მაგალითად name@example.com.',
      emailTaken: 'ეს ელფოსტა უკვე გამოყენებულია. სცადეთ შესვლა ან პაროლის აღდგენა.',
      nameRequirement: 'სახელი უნდა შეიცავდეს სულ მცირე 3 სიმბოლოს.',
      surnameRequirement: 'გვარი უნდა შეიცავდეს სულ მცირე 3 სიმბოლოს.',
      passwordRequirement: 'მინიმუმ 8 სიმბოლო: პატარა და დიდი ლათინური ასო და ციფრი.',
      passwordRepeatHint: 'ხელახლა შეიყვანეთ იგივე პაროლი.',
      passwordRepeatRequired: 'გაიმეორეთ პაროლი.',
      nameValid: 'სახელი სწორად არის შეყვანილი.',
      surnameValid: 'გვარი სწორად არის შეყვანილი.',
      personalIdValid: 'პირადი ნომერი სწორად არის შეყვანილი.',
      phoneValid: 'ტელეფონის ნომერი სწორად არის შეყვანილი.',
      emailValid: 'ელფოსტის ფორმატი სწორია.',
      passwordValid: 'პაროლი აკმაყოფილებს მოთხოვნებს.',
      passwordsMatch: 'პაროლები ემთხვევა.',
      copyPassword: 'კოპირება',
      passwordCopied: 'დაკოპირდა',
      invalidCredentials: 'მონაცემები არასწორია',
      registerFailed: 'რეგისტრაცია ვერ მოხერხდა',
      passwordsMismatch: 'პაროლები არ ემთხვევა',
      mustAgree: 'გთხოვთ დაეთანხმოთ წესებსა და პირობებს',
      agreePrefix: 'ვეთანხმები',
      termsLink: 'წესებსა და პირობებს',
      noAccount: 'არ გაქვს ანგარიში?',
      haveAccount: 'უკვე გაქვს ანგარიში?',
      forgotPassword: 'დაგავიწყდა პაროლი?',
      forgotPasswordHint: 'შეიყვანე შენი ელფოსტა და გამოგიგზავნით პაროლის აღდგენის ბმულს.',
      sendResetLink: 'აღდგენის ბმულის გაგზავნა',
      sending: 'იგზავნება…',
      resetLinkSent: 'პაროლის აღდგენის ბმული გამოგზავნილია ელფოსტაზე.',
      resetLinkFailed: 'ბმულის გაგზავნა ვერ მოხერხდა',
      resetPasswordTitle: 'პაროლის აღდგენა',
      resetPasswordCta: 'პაროლის შენახვა',
      resetFailed: 'პაროლის აღდგენა ვერ მოხერხდა',
      resetSuccess: 'პაროლი წარმატებით განახლდა.',
      saving: 'ინახება…',
      backToLogin: 'შესვლის გვერდზე დაბრუნება',
      backToAccount: 'ანგარიშზე დაბრუნება',
      orContinueWith: 'ან გააგრძელე',
      continueWithGoogle: 'Google-ით შესვლა',
      continueWithFacebook: 'Facebook-ით შესვლა',
      socialFailed: 'სოციალური ავტორიზაცია ვერ მოხერხდა',
      redirecting: 'გადამისამართება…',
    },
    account: {
      title: 'ჩემი ანგარიში',
      customerInformation: 'მომხმარებლის ინფორმაცია',
      passwordManagement: 'პაროლის მართვა',
      ordersTitle: 'შეკვეთები',
      ordersEmpty: 'ჯერ არ გაქვს შეკვეთები',
      ordersLoading: 'იტვირთება…',
      ordersError: 'შეკვეთების ჩატვირთვა ვერ მოხერხდა',
      ordersEmptyDescription: 'აირჩიე სასურველი პროდუქტი და შენი შეკვეთები აქ გამოჩნდება.',
      browseProducts: 'კატალოგის ნახვა',
      ordersCount_one: '{{count}} შეკვეთა',
      ordersCount_other: '{{count}} შეკვეთა',
      orderNumber: 'შეკვეთა #{{number}}',
      itemsCount_one: '{{count}} პროდუქტი',
      itemsCount_other: '{{count}} პროდუქტი',
      viewDetails: 'დეტალების ნახვა',
      hideDetails: 'დეტალების დამალვა',
      detailsLoading: 'შეკვეთის დეტალები იტვირთება…',
      detailsError: 'შეკვეთის დეტალების ჩატვირთვა ვერ მოხერხდა.',
      retry: 'თავიდან ცდა',
      orderItems: 'პროდუქტები',
      orderProgress: 'შეკვეთის სტატუსი',
      progressNote:
        'ნაჩვენებია მაღაზიის მიმდინარე სტატუსი; გადამზიდავის თვალთვალი ხელმისაწვდომი არ არის.',
      orderSupport: 'კითხვა ამ შეკვეთაზე',
      itemsUnavailable: 'ამ შეკვეთის პროდუქტების სია მიუწვდომელია.',
      deliveryAddress: 'მიტანის მისამართი',
      paymentMethod: 'გადახდის მეთოდი',
      orderTotal: 'ჯამი',
      ordersPagination: 'შეკვეთების გვერდები',
      previousPage: 'წინა',
      nextPage: 'შემდეგი',
      seeMore: 'მეტის ნახვა',
      pageOf: 'გვერდი {{page}} / {{pages}}',
      'payment.cod': 'ნაღდი ანგარიშსწორება',
      'payment.card': 'ბარათი',
      'payment.bog': 'საქართველოს ბანკი',
      'payment.bog-card': 'საქართველოს ბანკის ბარათი',
      'payment.bog-apple-pay': 'Apple Pay',
      'payment.bog-google-pay': 'Google Pay',
      'payment.bog-mobile-bank': 'მობილური ბანკი',
      'payment.bog-loyalty': 'PLUS / MR ქულები',
      'payment.bog-installment': 'საქართველოს ბანკის განვადება',
      'payment.bog-bnpl': 'ნაწილ-ნაწილ',
      'payment.tbc': 'თიბისი ბანკი',
      'payment.tbc-installment': 'თიბისი განვადება',
      'payment.space': 'Space ბანკი',
      'payment.tera': 'ტერა ბანკი',
      'status.pending': 'მოლოდინში',
      'status.awaiting-payment': 'გადახდის მოლოდინში',
      'status.paid': 'გადახდილი',
      'status.processing': 'მუშავდება',
      'status.shipped': 'გაგზავნილი',
      'status.delivered': 'მიწოდებული',
      'status.cancelled': 'გაუქმებული',
      'status.refunded': 'დაბრუნებული',
      profileEdit: 'პროფილის რედაქტირება',
      personalInformation: 'პირადი ინფორმაცია',
      profileDescription: 'განაახლე შენი საკონტაქტო და მისამართის ინფორმაცია.',
      profileLoading: 'პირადი ინფორმაცია იტვირთება…',
      profileLoadError: 'პირადი ინფორმაციის ჩატვირთვა ვერ მოხერხდა.',
      name: 'სახელი',
      surname: 'გვარი',
      personalId: 'პირადი ნომერი',
      address: 'მისამართი',
      phone: 'ტელეფონი',
      email: 'ელფოსტა',
      changePassword: 'პაროლის შეცვლა',
      passwordOptional: 'შეავსე მხოლოდ იმ შემთხვევაში, თუ პაროლის შეცვლა გსურს.',
      passwordSeparateHint:
        'პაროლის შეცვლა არასავალდებულოა და პირადი მონაცემებისგან დამოუკიდებლად ინახება.',
      requiredField: 'ეს ველი სავალდებულოა.',
      fieldValid: 'სწორად არის შეყვანილი.',
      passwordValid: 'პაროლის სიგრძე სწორია.',
      passwordsMatch: 'პაროლები ემთხვევა.',
      personalIdInvalid: 'პირადი ნომერი უნდა შეიცავდეს ზუსტად 11 ციფრს.',
      phoneInvalid: 'ტელეფონი უნდა შეიცავდეს ზუსტად 9 ციფრს.',
      emailInvalid: 'შეიყვანეთ სწორი ელფოსტა.',
      emailHint: 'მაგალითად: name@example.com',
      emailTaken: 'ეს ელფოსტა უკვე გამოყენებულია.',
      currentPasswordRequired: 'შეიყვანეთ ამჟამინდელი პაროლი.',
      currentPasswordInvalid: 'ამჟამინდელი პაროლი არასწორია.',
      newPasswordRequirement: 'ახალი პაროლი უნდა შეიცავდეს მინიმუმ 8 სიმბოლოს.',
      passwordsMismatch: 'პაროლები არ ემთხვევა.',
      passwordChanged: 'პაროლი განახლდა.',
      passwordChangeError: 'პაროლის შეცვლა ვერ მოხერხდა. შეამოწმეთ ველები და სცადეთ ხელახლა.',
      passwordSetupTitle: 'ანგარიშის პაროლი',
      socialPasswordHint:
        'ეს ანგარიში სოციალური ქსელით შეიქმნა და ამჟამინდელი პაროლი არ აქვს. ელფოსტაზე აღდგენის ბმულის გაგზავნით შეგიძლიათ პაროლის დაყენება. პირადი მონაცემები ცალკე შეინახეთ.',
      setPasswordLink: 'პაროლის დაყენება',
      passwordLinkSending: 'ბმული იგზავნება…',
      passwordLinkSent: 'პაროლის დასაყენებელი ბმული გამოგზავნილია თქვენს ელფოსტაზე.',
      passwordLinkFailed: 'ბმულის გაგზავნა ვერ მოხერხდა. სცადეთ ხელახლა.',
      currentPassword: 'ამჟამინდელი პაროლი',
      newPassword: 'ახალი პაროლი',
      newPasswordConfirm: 'გაიმეორე ახალი პაროლი',
      save: 'შენახვა',
      saving: 'ინახება…',
      profileSaved: 'პროფილი განახლდა.',
      profileSaveError: 'შენახვა ვერ მოხერხდა.',
    },
    static: {
      'about.title': 'ჩვენ შესახებ',
      'about.subtitle': 'მცირე ინფორმაცია SSHOP-ის შესახებ',
      'news.title': 'სიახლეები',
      'news.empty': 'სიახლეები მალე გამოჩნდება',
      'news.readMore': 'მეტის წაკითხვა',
      'news.seeMore': 'მეტის ნახვა',
      'news.previous': 'წინა',
      'news.next': 'შემდეგი',
      'news.published': 'გამოქვეყნდა',
      'contact.title': 'კონტაქტი',
      'contact.subtitle': 'დაგვიკავშირდი',
      'contact.address': 'მისამართი',
      'contact.phone': 'ტელეფონი',
      'contact.email': 'ელფოსტა',
      'contact.hours': 'სამუშაო საათები',
      'contact.formName': 'სახელი',
      'contact.formEmail': 'ელფოსტა',
      'contact.formPhone': 'ტელეფონი',
      'contact.formMessage': 'შეტყობინება',
      'contact.formSubmit': 'გაგზავნა',
      'contact.signInToSend': 'შეტყობინების გასაგზავნად შედით ანგარიშში',
      'contact.formSending': 'იგზავნება…',
      'contact.formSuccess': 'მადლობა! შეტყობინება გაიგზავნა.',
      'contact.formError': 'შეცდომა, სცადეთ თავიდან.',
      'guarantee.title': 'გარანტია',
      'guarantee.subtitle': 'ვისარგებლე ჩვენი გარანტიით',
      'shippingPayment.title': 'მიწოდება და გადახდა',
      'privacy.title': 'კონფიდენციალურობის პოლიტიკა',
      'terms.title': 'წესები და პირობები',
    },
  },
  en: {
    common: {
      'nav.home': 'Home',
      'nav.catalog': 'Catalog',
      'nav.categories': 'Categories',
      'nav.allCategories': 'All categories',
      'nav.allProducts': 'All products',
      'nav.deals': 'Deals',
      'nav.categoriesNoSubs': 'No subcategories yet',
      'nav.categoriesCount_one': '{{count}} product',
      'nav.categoriesCount_other': '{{count}} products',
      'nav.categoriesCount': '{{count}} products',
      'nav.categoriesPickSub': 'Pick a subcategory',
      'nav.categoriesFeatured': 'Featured offer',
      'nav.categoriesHint': 'Hover a category to explore',
      'nav.categoriesSeeAllSubs': 'See all subcategories',
      'nav.categoriesViewAllProducts': 'View all products',
      'nav.back': 'Back',
      'nav.close': 'Close',
      'nav.cart': 'Cart',
      'nav.compare': 'Compare products',
      'nav.compareCount': 'Compare ({{count}})',
      'nav.about': 'About',
      'nav.news': 'News',
      'nav.contact': 'Contact',
      'nav.search': 'Search',
      'nav.favorites': 'Favorites',
      'footer.tagline': 'Your trusted online marketplace.',
      'footer.shop': 'Shop',
      'footer.browse': 'Browse',
      'footer.info': 'Information',
      'footer.legal': 'Legal',
      'footer.guarantee': 'Guarantee',
      'footer.shippingPayment': 'Shipping & payment',
      'footer.privacy': 'Privacy policy',
      'footer.terms': 'Terms & conditions',
      'footer.contact': 'Get in touch',
      'footer.contactHint': 'Contact information will be available soon.',
      'footer.shopDeals': 'Shop the deals',
      'footer.rights': 'All rights reserved.',
      'search.empty': 'No products found',
      'search.loading': 'Searching…',
      'search.viewAll': 'View all results',
      'search.recent': 'Recent searches',
      'search.clearHistory': 'Clear history',
      'search.noRecent': 'No recent searches',
      'wishlist.title': 'Favorites',
      'wishlist.subtitle': 'Products you saved for later.',
      'wishlist.empty': 'Your wishlist is empty',
      'wishlist.moveAllToCart': 'Move all to cart',
      'wishlist.emptyHint': 'Tap the heart icon on any product to save it here.',
      'wishlist.browseCatalog': 'Browse the catalog',
      'wishlist.error': 'Could not load your wishlist',
      'theme.toggle': 'Toggle theme',
      'theme.label': 'Theme',
      'theme.light': 'Light',
      'theme.dark': 'Dark',
      'theme.system': 'System',
      'app.loading': 'Loading…',
      'app.error': 'Something went wrong',
      'app.notFound': 'Page not found',
      'app.retry': 'Try again',
      'auth.login': 'Sign in',
      'auth.logout': 'Sign out',
      accountDetails: 'Account details',
      'account.menu': 'Account',
      'account.preferences': 'Preferences',
      'language.label': 'Language',
      'language.english': 'English',
      'language.georgian': 'Georgian',
      'language.shortGe': 'ქარ',
      'language.shortEn': 'EN',
      'language.switchTo': 'Switch language to {{language}}',
      'cookies.preferences': 'Cookie preferences',
      'cookies.description':
        'We use analytics and advertising cookies only with your consent. Essential functions work without consent.',
      'cookies.reject': 'Reject',
      'cookies.accept': 'Accept',
      'home.hero.badge': 'New collection',
      'home.hero.title': 'Shop smart. Shop SSHOP.',
      'home.hero.subtitle':
        'Georgia‑first commerce — thousands of products, fast delivery, and flexible payment, all in one place.',
      'home.hero.ctaPrimary': 'Browse catalog',
      'home.hero.ctaSecondary': 'View deals',
      'home.trust.shipping.title': 'Delivery & payment',
      'home.trust.shipping.body': 'Delivery and payment details',
      'home.trust.guarantee.title': 'Guarantee',
      'home.trust.guarantee.body': 'Warranty information',
      'home.trust.privacy.title': 'Privacy policy',
      'home.trust.privacy.body': 'About your personal data',
      'home.trust.terms.title': 'Terms & conditions',
      'home.trust.terms.body': 'Review the terms of purchase',
      'home.trust.returns.title': '14-day returns',
      'home.trust.returns.body': 'Hassle-free, no charge',
      'home.trust.support.title': '24/7 support',
      'home.trust.support.body': 'We are always here to help',
      'home.trust.secure.title': 'Secure payments',
      'home.trust.secure.body': 'Bank-grade transaction protection',
      'home.categories.title': 'Shop by category',
      'home.categories.subtitle': 'Find what you need, fast.',
      'home.categories.viewAll': 'View all categories',
      'home.categories.error': 'Failed to load categories',
      'categories.subtitle': 'Choose a category to browse its products.',
      'categories.empty': 'No categories are available yet.',
      'home.featured.subtitle': 'Best deals and latest arrivals.',
      'home.featured.viewAll': 'View all',
      'home.carousel.promotions': 'Promotions',
      'home.carousel.slide': 'Slide {{number}}',
      'home.carousel.previous': 'Previous slide',
      'home.carousel.next': 'Next slide',
      'home.carousel.selector': 'Slide selector',
      'home.carousel.goTo': 'Go to slide {{number}}',
      'home.carousel.play': 'Play slideshow',
      'home.carousel.pause': 'Pause slideshow',
      'home.values.shipping.title': 'Fast delivery',
      'home.values.shipping.body': 'Across Georgia within 1–3 business days.',
      'home.values.returns.title': 'Easy returns',
      'home.values.returns.body': '14 days to return any item, no questions asked.',
      'home.values.support.title': 'Always-on support',
      'home.values.support.body': 'Our team is available seven days a week.',
      'home.featured.title': 'Featured products',
      'home.featured.seeAll': 'See all',
      'home.featured.placeholderCategory': 'Category',
      'home.featured.placeholderName': 'Product name',
      'home.bestPrices': 'Best prices',
      'home.bestPricesSubtitle': 'Hand-picked deals and discounts',
      'home.popular': 'Popular products',
      'home.popularSubtitle': 'Customer favorites right now',
      'home.newArrivals': 'New arrivals',
      'home.newArrivalsSubtitle': 'Recently added to the catalog',
      'home.partners': 'Our partners',
      'home.seeAll': 'See all',
      'home.learnMore': 'Learn more',
    },
    catalog: {
      title: 'Catalog',
      results: '{{count}} products',
      noResults: 'No products found',
      noResultsHelp: 'Try another search term or remove some filters.',
      browseAll: 'Browse all products',
      compareProducts: 'Compare products',
      compareCount: '{{count}} products selected for comparison',
      clearComparison: 'Clear comparison',
      compareSameCategory: 'Choose products from the same category.',
      compareLimit:
        'There are already {{count}} products in your compare list. Remove one before adding another.',
      compareNeedTwo: 'Select at least 2 products from the same category to compare.',
      compareDifferences: 'Show differences only',
      compareSpecification: 'Specification',
      comparePrice: 'Price',
      compareStock: 'Stock',
      removeCompare: 'Remove',
      filters: 'Filters',
      applyFilters: 'Apply filters',
      activeFilters: '{{count}} active filters',
      removeFilter: 'Remove filter',
      clearFilters: 'Clear filters',
      sortBy: 'Sort by',
      'sort.relevance': 'Relevance',
      'sort.popularity': 'Most popular',
      'sort.priceAsc': 'Price: low to high',
      'sort.priceDesc': 'Price: high to low',
      'sort.newest': 'Newest',
      'sort.oldest': 'Oldest',
      priceRange: 'Price',
      priceMin: 'Min',
      priceMax: 'Max',
      brand: 'Brand',
      category: 'Category',
      allCategories: 'All categories',
      allProducts: 'All products',
      inStockOnly: 'In stock only',
      filterPopular: 'Popular',
      filterSpecial: 'On sale',
      filterNew: 'New arrivals',
      subcategories: 'Subcategories',
      categoryDescription: 'About this category',
      showMore: 'Show more',
      showLess: 'Show less',
      resetColor: 'Clear color',
      perPage: 'Per page',
      page: 'Page {{page}}',
      prev: 'Previous',
      next: 'Next',
      loading: 'Loading products…',
      errorLoading: 'Failed to load products',
    },
    product: {
      variants: 'Variants',
      chooseColor: 'Choose a color',
      compare: 'Compare',
      removeCompare: 'Remove from comparison',
      compareSameCategory: 'Choose products from the same category.',
      compareLimit:
        'There are already {{count}} products in your compare list. Remove one before adding another.',
      imageEnlarge: 'Enlarge image: {{title}}',
      imagePrevious: 'Previous image',
      imageNext: 'Next image',
      imageClose: 'Close image',
      warrantyInfo: 'Warranty information',
      deliveryHelp: 'Delivery questions',
      addToCart: 'Add to cart',
      addedToCart: 'Added to cart',
      addToWishlist: 'Add to wishlist',
      removeFromWishlist: 'Remove from wishlist',
      addedToWishlist: 'Added to wishlist',
      outOfStock: 'Out of stock',
      inStock: 'In stock',
      lowStock: 'Low stock',
      'stock.count': '{{count}} in stock',
      brand: 'Brand',
      sku: 'SKU',
      description: 'Description',
      specs: 'Specifications',
      keySpecs: 'Key specifications',
      viewAllSpecs: 'See all specifications',
      showMore: 'Show more',
      showLess: 'Show less',
      reviews: 'Reviews',
      rating: 'Rating',
      'rating.count': '{{count}} reviews',
      share: 'Share',
      relatedProducts: 'Related products',
      similarProducts: 'You may also like',
      viewMore: 'View more',
      freeShipping: 'Free shipping',
      quantity: 'Quantity',
      installment: 'Installment',
      buyNow: 'Buy now',
      notFound: 'Product not found',
      home: 'Home',
      catalog: 'Catalog',
      copyLink: 'Copy link',
      linkCopied: 'Link copied',
    },
    cart: {
      title: 'Your cart',
      empty: 'Your cart is empty',
      emptyCta: 'Start shopping',
      itemCount_one: '{{count}} item',
      itemCount_other: '{{count}} items',
      subtotal: 'Subtotal',
      discount: 'Discount',
      shipping: 'Shipping',
      shippingCalculatedAtCheckout: 'Calculated after you choose a city',
      tax: 'Tax',
      total: 'Total',
      totalBeforeShipping: 'Total before shipping',
      estimatedTotal: 'Estimated total',
      free: 'Free',
      checkout: 'Checkout',
      continueShopping: 'Continue shopping',
      removeItem: 'Remove',
      updateQty: 'Update quantity',
      applyPromo: 'Apply promo code',
      promoCode: 'Promo code',
      promoCodePlaceholder: 'Enter code',
      promoInvalid: 'Promo code is invalid',
      promoApplied: 'Promo code applied',
      removePromo: 'Remove promo',
      orderSummary: 'Order summary',
      saving: 'Saving…',
      errorUpdating: 'Failed to update cart',
    },
    checkout: {
      title: 'Checkout',
      estimateDelivery: 'Check delivery cost',
      deliveryCost: 'Delivery',
      deliveryCostAfterCity: 'Choose a city to see the delivery cost.',
      contactInfo: 'Contact info',
      shippingAddress: 'Shipping address',
      paymentMethod: 'Payment method',
      choosePaymentMethod: 'Choose a payment method',
      orderSummary: 'Order summary',
      recipientName: 'Recipient name',
      phone: 'Phone',
      email: 'Email',
      country: 'Country',
      city: 'City',
      selectCity: 'Select a city',
      street: 'Street address',
      pinDeliveryLocation: 'Pin the exact delivery location',
      pinDeliveryLocationHint:
        'Help the courier find you faster. Search or tap the map to place the pin.',
      optional: 'Optional',
      searchAddress: 'Search for an address',
      searchAddressAction: 'Search address',
      addressSuggestions: 'Address suggestions',
      noAddressResults: 'No address found. Try a different search.',
      useMyLocation: 'Use my location',
      deliveryMap: 'Delivery address map',
      mapLoading: 'Loading map…',
      mapUnavailable: 'Map temporarily unavailable',
      mapUnavailableHint:
        'Enter the delivery address in the field above. You can still complete checkout.',
      tapMapToPin: 'Tap the map to place the pin',
      mapPinTitle: 'Delivery location',
      searchingAddress: 'Searching for an address…',
      findingLocation: 'Finding your location…',
      findingAddress: 'Finding the address…',
      mapLocationError: 'We could not determine this location. You can enter the address manually.',
      locationConfirmed: 'Location pinned',
      removePin: 'Remove',
      apartment: 'Apartment / suite',
      postalCode: 'Postal code',
      notes: 'Order notes',
      placeOrder: 'Proceed to payment',
      processing: 'Processing…',
      preparingPayment: 'Preparing your payment…',
      redirectingToBank: 'Redirecting to the bank…',
      paymentCod: 'Cash on delivery',
      paymentBog: 'Bank of Georgia',
      paymentBogCard: 'Bank of Georgia card',
      paymentApplePay: 'Apple Pay',
      paymentGooglePay: 'Google Pay',
      paymentMobileBank: 'BOG Mobile Bank',
      paymentBogBnpl: 'Pay in parts',
      paymentBogLoyalty: 'MR / PLUS',
      paymentTbc: 'TBC Bank',
      paymentBogInstallment: 'BOG installment',
      paymentTbcInstallment: 'TBC installment',
      paymentSpace: 'Space Bank',
      paymentTera: 'Tera Bank',
      paymentGroupDirect: 'Pay by card',
      paymentGroupInstallment: 'Installment',
      paymentGroupBnpl: 'Buy now, pay later',
      paymentGroupLoyalty: 'Loyalty',
      selected: 'Selected',
      savedCard: 'Card',
      newCard: 'New card',
      saveCard: 'Save this card',
      removeSavedCard: 'Remove saved card',
      acceptTerms: 'I accept the',
      termsAndConditions: 'terms and conditions',
      requiredField: 'This field is required',
      selectPaymentMethod: 'Choose a payment method',
      acceptTermsError: 'Accept the terms and conditions before payment',
      paymentUnavailable: 'The payment service is temporarily unavailable. Please try again.',
      bnplUnavailable: 'BOG Parts is currently unavailable. Please choose another payment method.',
      phoneInvalid: 'Enter a valid phone number',
      emailInvalid: 'Enter a valid email address',
      cartEmpty: 'Your cart is empty',
      errorPlacingOrder: 'Failed to place order',
      paymentFailed: 'The payment was not completed. Please try again.',
      paymentPending: 'The payment is still processing. This page checks its status automatically.',
      successTitle: 'Order received',
      successBody: 'Your order number is {{number}}',
      paidTitle: 'Payment confirmed',
      paidBody: 'Your order is being processed.',
      paymentFailedTitle: 'Payment failed',
      paymentFailedBody:
        'This order has not been paid. Your items remain in the cart. Return to checkout or contact us with your order number for help.',
      paymentPendingTitle: 'Checking payment',
      codPendingBody: 'Your order is received. You will pay on delivery.',
      checkingPayment: 'Checking your order status…',
      unverifiedTitle: 'Unable to verify this order',
      unverifiedBody: 'This link cannot verify the order status. Contact us for help.',
      contactSupport: 'Contact us',
      backToCheckout: 'Return to checkout',
      backToCatalog: 'Continue shopping',
    },
    auth: {
      signIn: 'Sign in',
      close: 'Close',
      checkoutPrompt: 'Sign in or create an account to continue to checkout.',
      signUp: 'Sign up',
      createAccount: 'Create account',
      signingIn: 'Signing in…',
      signingUp: 'Creating account…',
      email: 'Email',
      password: 'Password',
      passwordRepeat: 'Repeat password',
      firstName: 'First name',
      lastName: 'Last name',
      personalId: 'Personal ID',
      phone: 'Phone',
      requiredField: 'This field is required.',
      personalIdInvalid: 'Personal ID must contain exactly 11 digits',
      phoneInvalid: 'Phone must contain exactly 9 digits',
      emailInvalid: 'Enter a valid email address',
      emailHint: 'Use a working email address, such as name@example.com.',
      emailTaken: 'This email is already in use. Sign in or reset your password.',
      nameRequirement: 'First name must contain at least 3 characters.',
      surnameRequirement: 'Last name must contain at least 3 characters.',
      passwordRequirement:
        'At least 8 characters with a lowercase letter, uppercase letter, and number.',
      passwordRepeatHint: 'Enter the same password again.',
      passwordRepeatRequired: 'Please repeat your password.',
      nameValid: 'First name looks good.',
      surnameValid: 'Last name looks good.',
      personalIdValid: 'Personal ID looks good.',
      phoneValid: 'Phone number looks good.',
      emailValid: 'Email format looks good.',
      passwordValid: 'Password meets the requirements.',
      passwordsMatch: 'Passwords match.',
      copyPassword: 'Copy',
      passwordCopied: 'Copied',
      invalidCredentials: 'Invalid email or password',
      registerFailed: 'Could not create account',
      passwordsMismatch: 'Passwords do not match',
      mustAgree: 'Please accept the terms and conditions',
      agreePrefix: 'I agree to the',
      termsLink: 'terms and conditions',
      noAccount: "Don't have an account?",
      haveAccount: 'Already have an account?',
      forgotPassword: 'Forgot password?',
      forgotPasswordHint: 'Enter your email and we will send you a link to reset your password.',
      sendResetLink: 'Send reset link',
      sending: 'Sending…',
      resetLinkSent: 'Password reset link sent to your email.',
      resetLinkFailed: 'Could not send reset link',
      resetPasswordTitle: 'Reset password',
      resetPasswordCta: 'Save new password',
      resetFailed: 'Could not reset password',
      resetSuccess: 'Password updated successfully.',
      saving: 'Saving…',
      backToLogin: 'Back to sign in',
      backToAccount: 'Back to account',
      orContinueWith: 'or continue with',
      continueWithGoogle: 'Continue with Google',
      continueWithFacebook: 'Continue with Facebook',
      socialFailed: 'Social sign in failed',
      redirecting: 'Redirecting…',
    },
    account: {
      title: 'My account',
      customerInformation: 'Customer information',
      passwordManagement: 'Password management',
      ordersTitle: 'Orders',
      ordersEmpty: "You don't have any orders yet",
      ordersLoading: 'Loading…',
      ordersError: 'Failed to load orders',
      ordersEmptyDescription: 'Find something you love and your orders will appear here.',
      browseProducts: 'Browse products',
      ordersCount_one: '{{count}} order',
      ordersCount_other: '{{count}} orders',
      orderNumber: 'Order #{{number}}',
      itemsCount_one: '{{count}} item',
      itemsCount_other: '{{count}} items',
      viewDetails: 'View details',
      hideDetails: 'Hide details',
      detailsLoading: 'Loading order details…',
      detailsError: 'Could not load order details.',
      retry: 'Try again',
      orderItems: 'Items',
      orderProgress: 'Order progress',
      progressNote: 'This shows the shop status; carrier tracking is not available.',
      orderSupport: 'Ask about this order',
      itemsUnavailable: 'Items for this order are unavailable.',
      deliveryAddress: 'Delivery address',
      paymentMethod: 'Payment method',
      orderTotal: 'Total',
      ordersPagination: 'Order pages',
      previousPage: 'Previous',
      nextPage: 'Next',
      seeMore: 'See more',
      pageOf: 'Page {{page}} of {{pages}}',
      'payment.cod': 'Cash on delivery',
      'payment.card': 'Card',
      'payment.bog': 'Bank of Georgia',
      'payment.bog-card': 'Bank of Georgia card',
      'payment.bog-apple-pay': 'Apple Pay',
      'payment.bog-google-pay': 'Google Pay',
      'payment.bog-mobile-bank': 'Mobile bank',
      'payment.bog-loyalty': 'PLUS / MR points',
      'payment.bog-installment': 'BOG installment',
      'payment.bog-bnpl': 'BOG Parts',
      'payment.tbc': 'TBC Bank',
      'payment.tbc-installment': 'TBC installment',
      'payment.space': 'Space Bank',
      'payment.tera': 'Tera Bank',
      'status.pending': 'Pending',
      'status.awaiting-payment': 'Awaiting payment',
      'status.paid': 'Paid',
      'status.processing': 'Processing',
      'status.shipped': 'Shipped',
      'status.delivered': 'Delivered',
      'status.cancelled': 'Cancelled',
      'status.refunded': 'Refunded',
      profileEdit: 'Edit profile',
      personalInformation: 'Personal information',
      profileDescription: 'Keep your contact and address details up to date.',
      profileLoading: 'Loading personal information…',
      profileLoadError: 'Could not load personal information.',
      name: 'Name',
      surname: 'Surname',
      personalId: 'Personal ID number',
      address: 'Address',
      phone: 'Phone',
      email: 'Email',
      changePassword: 'Change password',
      passwordOptional: 'Fill this in only if you want to change your password.',
      passwordSeparateHint:
        'Changing your password is optional and saves separately from your personal details.',
      requiredField: 'This field is required.',
      fieldValid: 'Looks good.',
      passwordValid: 'Password length looks good.',
      passwordsMatch: 'Passwords match.',
      personalIdInvalid: 'Personal ID must contain exactly 11 digits.',
      phoneInvalid: 'Phone must contain exactly 9 digits.',
      emailInvalid: 'Enter a valid email address.',
      emailHint: 'For example: name@example.com',
      emailTaken: 'This email is already in use.',
      currentPasswordRequired: 'Enter your current password.',
      currentPasswordInvalid: 'The current password is incorrect.',
      newPasswordRequirement: 'Your new password must contain at least 8 characters.',
      passwordsMismatch: 'Passwords do not match.',
      passwordChanged: 'Password updated.',
      passwordChangeError: 'Could not change password. Check the fields and try again.',
      passwordSetupTitle: 'Account password',
      socialPasswordHint:
        'This account was created with social sign in and has no current password. Use the email reset link to set one. Save your personal details separately.',
      setPasswordLink: 'Set a password',
      passwordLinkSending: 'Sending link…',
      passwordLinkSent: 'A password setup link has been sent to your email.',
      passwordLinkFailed: 'Could not send the link. Please try again.',
      currentPassword: 'Current password',
      newPassword: 'New password',
      newPasswordConfirm: 'Confirm new password',
      save: 'Save',
      saving: 'Saving…',
      profileSaved: 'Profile updated.',
      profileSaveError: 'Failed to save.',
    },
    static: {
      'about.title': 'About us',
      'about.subtitle': 'A little about SSHOP',
      'news.title': 'News',
      'news.empty': 'News coming soon',
      'news.readMore': 'Read more',
      'news.seeMore': 'See more',
      'news.previous': 'Previous',
      'news.next': 'Next',
      'news.published': 'Published',
      'contact.title': 'Contact',
      'contact.subtitle': 'Get in touch',
      'contact.address': 'Address',
      'contact.phone': 'Phone',
      'contact.email': 'Email',
      'contact.hours': 'Hours',
      'contact.formName': 'Name',
      'contact.formEmail': 'Email',
      'contact.formPhone': 'Phone',
      'contact.formMessage': 'Message',
      'contact.formSubmit': 'Send',
      'contact.signInToSend': 'Sign in to send a message',
      'contact.formSending': 'Sending…',
      'contact.formSuccess': 'Thank you! Your message has been sent.',
      'contact.formError': 'Error, please try again.',
      'guarantee.title': 'Guarantee',
      'guarantee.subtitle': 'How our guarantee works',
      'shippingPayment.title': 'Shipping & payment',
      'privacy.title': 'Privacy policy',
      'terms.title': 'Terms & conditions',
    },
  },
};

const namespaces = [
  'common',
  'catalog',
  'product',
  'cart',
  'checkout',
  'auth',
  'account',
  'static',
] as const;

export function I18nProvider({ children }: PropsWithChildren<object>) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    const routeLocale = window.location.pathname.split('/')[1];
    const initialize = async () => {
      await initI18n({
        defaultLocale: isLocaleCode(routeLocale) ? routeLocale : env.DEFAULT_LOCALE,
        resources,
        namespaces,
      });
      if (!active) return;
      setReady(true);

      try {
        const overrides = await api.translations.list();
        if (!active) return;
        for (const locale of ['ge', 'en'] as const) {
          for (const namespace of namespaces) {
            const entries = overrides[locale]?.[namespace];
            if (entries) i18nInstance.addResourceBundle(locale, namespace, entries, true, true);
          }
        }
      } catch {
        // Keep the bundled translations when the API is unavailable.
      }
    };
    void initialize();
    return () => {
      active = false;
    };
  }, []);

  if (!ready) {
    return <AppLoader label={env.DEFAULT_LOCALE === 'ge' ? 'იტვირთება…' : 'Loading…'} />;
  }
  return <>{children}</>;
}
